/**
 * Terrain generation: applies the learned style data to rasterized terrain shapes.
 * Pure (no DOM): used by the editor and by the node verification tools.
 */
import { ALL_LINES, LINE_B, LINE_BITS, LINE_L, LINE_R, LINE_S, LINE_T, foliageBit, foliageFields, type DecorationSettings, type EdgeArtMode, type FoliageKind, type FoliageLayer, type FoliagePaint, type GrassLines, type GrassPaint, type PaletteEntry, type ShadingSettings, type TerrainShape } from '../model/format.ts';
import { classifyCells, createGrid, paintRings, ringsBounds, sampleDistance, shapeKindAllowed, type SampleGrid } from './raster.ts';
import { EMPTY, FULL, SHAPES, composeFlags, insideShape, slopeSegment, transformShape } from './shapes.ts';
import { KEY_LEVELS, MIN_COUNT, NB, contextKeys, hasOpenSide, isEdgeZone, isInterior } from './keys.ts';
import { ALL_DIRECTIONS, FILL, NONE, type RuleSet, type StyleOverride, type TerrainStyleData, type ToneTile } from './types.ts';
import { BURIED, NB2, buriedNeighbours, caseKey, cornerSurfaces, exactCase, exposedHalves, exposedLines, generalCase, grassView, insideCorners, isExactCase, isToneCase, linesAtCorner, mirrorCase, partialCase, toneCase, type CaseLayer } from './cases.ts';
import { variantSprites } from './variants.ts';

// ------------------------------------------------------------------ compiled style

interface Dist {
  c: number[];
  w: number[];
  total: number;
}

interface Bigram {
  h: Map<number, Map<number, number>>;
  hTot: Map<number, number>;
  v: Map<number, Map<number, number>>;
  vTot: Map<number, number>;
  p: Map<number, number>;
}

interface CompiledStamp {
  index: number;
  /** shape the anchor cell must have (-1: no requirement on the anchor) */
  anchor: number;
  layer: 'level' | 'fg' | 'bg';
  tiles: number[];
  req: number[];
  rate: number;
  cover: number[];
  kind: FoliageKind;
  /** foliageBit(kind) */
  bit: number;
  /** offset of the solid cell nearest the anchor the stamp needs (the ground it stands on); null: none */
  root: [number, number] | null;
}

/** Offset of the required solid cell nearest the anchor; its polygon owns the decoration. */
function stampRoot(req: number[]): [number, number] | null {
  let best: [number, number] | null = null, bd = Infinity;
  for (let j = 0; j < req.length; j += 4) {
    if (req[j + 3] === 1 || req[j + 2] === EMPTY) continue;
    const d = Math.abs(req[j]) + Math.abs(req[j + 1]);
    if (d < bd) [best, bd] = [[req[j], req[j + 1]], d];
  }
  return best;
}

export interface CompiledStyle {
  data: TerrainStyleData;
  sprites: PaletteEntry[];
  directions: { s45: boolean; s21: boolean; s12: boolean };
  rules: { level: Map<string, Dist>; fg: Map<string, Dist> };
  /** edge art ending runs: level-3 context key + ':' + side of the bare neighbour */
  ends: Map<string, Dist>;
  bigram: { level: Bigram; fg: Bigram };
  fill: Dist;
  tones: (ToneTile & { w: number })[];
  /** dark region context -> tone tile */
  toneRules: Map<string, Dist>;
  /** tone choices that are plain base-tone tiles */
  lightTones: Set<number>;
  stamps: CompiledStamp[];
  /** stamps by required anchor shape (key -1: no anchor requirement) */
  stampsByAnchor: Map<number, CompiledStamp[]>;
  /** fallback level choice per shape class */
  byShape: Map<number, number>;
  defaultShading: ShadingSettings;
  defaultDecoration: DecorationSettings;
  /** hand-linked tile cases (cases.ts key -> choices) */
  cases: { dirt: Map<string, Dist>; grass: Map<string, Dist> };
  /** some case is linked for one exact neighbourhood (see exactCase) */
  hasExact: { dirt: boolean; grass: boolean };
  /** some case is linked for one light/dark neighbourhood (see toneCase) */
  hasTone: { dirt: boolean; grass: boolean };
  /** sprite indexes that count as dark: the plain fill and the dark shading tones */
  darkSprites: Set<number>;
  /** only linked cases are used, no learned pieces */
  manualOnly: boolean;
  /** an unlinked case uses its mirror image's link, flipped */
  mirror: boolean;
}

function isTerrainOnly(data: TerrainStyleData, tiles: number[]): boolean {
  for (let j = 2; j < tiles.length; j += 3) {
    const c = tiles[j];
    if (c < 0 || !((data.shapes[c >> 3] ?? -1) > EMPTY)) return false;
  }
  return true;
}

function dist(arr: number[] | undefined): Dist | null {
  if (!arr || !arr.length) return null;
  const d: Dist = { c: [], w: [], total: 0 };
  for (let i = 0; i + 1 < arr.length; i += 2) {
    if (arr[i + 1] <= 0) continue;
    d.c.push(arr[i]);
    d.w.push(arr[i + 1]);
    d.total += arr[i + 1];
  }
  return d.c.length ? d : null;
}

function compileBigram(rs: RuleSet): Bigram {
  const b: Bigram = { h: new Map(), hTot: new Map(), v: new Map(), vTot: new Map(), p: new Map() };
  const add = (m: Map<number, Map<number, number>>, tot: Map<number, number>, a: number, c: number, n: number) => {
    let r = m.get(a);
    if (!r) m.set(a, (r = new Map()));
    r.set(c, n);
    tot.set(a, (tot.get(a) ?? 0) + n);
  };
  for (let i = 0; i < rs.bigramH.length; i += 3) add(b.h, b.hTot, rs.bigramH[i], rs.bigramH[i + 1], rs.bigramH[i + 2]);
  for (let i = 0; i < rs.bigramV.length; i += 3) add(b.v, b.vTot, rs.bigramV[i], rs.bigramV[i + 1], rs.bigramV[i + 2]);
  let total = 0;
  for (let i = 0; i < rs.marginal.length; i += 2) total += rs.marginal[i + 1];
  for (let i = 0; i < rs.marginal.length; i += 2) b.p.set(rs.marginal[i], rs.marginal[i + 1] / Math.max(1, total));
  return b;
}

export function compileStyle(data: TerrainStyleData, ov?: StyleOverride | null): CompiledStyle {
  const sprites = [...data.sprites, ...(ov?.extraSprites ?? [])];
  const rules = { level: new Map<string, Dist>(), fg: new Map<string, Dist>() };
  for (const layer of ['level', 'fg'] as const) {
    const merged = { ...data[layer].rules, ...(ov?.rules?.[layer] ?? {}) };
    for (const [k, arr] of Object.entries(merged)) {
      const d = dist(arr);
      if (d) rules[layer].set(k, d);
    }
  }
  const ends = new Map<string, Dist>();
  for (const [k, arr] of Object.entries(data.fg.ends ?? {})) {
    const d = dist(arr);
    if (d) ends.set(k, d);
  }
  const fill = dist(ov?.fill ?? data.fill) ?? { c: [], w: [], total: 0 };
  const allTones = (data.tones?.tiles ?? []).map((t, i) => ({ ...t, w: ov?.toneWeights?.[i] ?? t.w }));
  const tones = allTones.filter((t) => t.w > 0);
  const toneOff = new Set(allTones.filter((t) => t.w <= 0).map((t) => t.c));
  const toneRules = new Map<string, Dist>();
  for (const [k, arr] of Object.entries({ ...(data.tones?.rules ?? {}), ...(ov?.rules?.tones ?? {}) })) {
    const d = dist(arr.flatMap((v, i) => (i % 2 === 0 && !toneOff.has(v) ? [v, arr[i + 1]] : [])));
    if (d) toneRules.set(k, d);
  }
  const lightTones = new Set(tones.filter((t) => t.s === EMPTY && Math.max(...t.g) < 0.15).map((t) => t.c));
  const stamps: CompiledStamp[] = [];
  data.stamps.forEach((s, i) => {
    const o = ov?.stamps?.[i];
    if (o?.off || s.off) return;
    const rate = o?.rate ?? s.rate;
    if (rate <= 0) return;
    // a foreground / background "stamp" made only of solid terrain sprites is terrain someone drew on
    // another layer (e.g. a strip of surface tiles on top of the ground), not a decoration
    if (s.layer !== 'level' && s.tiles.length && isTerrainOnly(data, s.tiles)) return;
    let anchor = -1;
    for (let j = 0; j < s.req.length; j += 4) if (s.req[j] === 0 && s.req[j + 1] === 0) anchor = s.req[j + 3] === 1 ? EMPTY : s.req[j + 2];
    const kind = o?.kind ?? s.kind ?? 'props';
    stamps.push({ index: i, anchor, layer: s.layer, tiles: s.tiles, req: s.req, rate, cover: s.cover, kind, bit: foliageBit(kind), root: stampRoot(s.req) });
  });
  // fallback choice per shape: most frequent learned level sprite of that shape
  const byShape = new Map<number, number>();
  const best = new Map<number, number>();
  const lm = data.level.marginal;
  for (let i = 0; i < lm.length; i += 2) {
    const ch = lm[i];
    if (ch < 0) continue;
    const sh = data.shapes[ch >> 3];
    if (sh === undefined || sh < 0) continue;
    const f = ch & 7;
    const shape = f ? transformShape(sh, f) : sh;
    if ((best.get(shape) ?? -1) < lm[i + 1]) {
      best.set(shape, lm[i + 1]);
      byShape.set(shape, ch);
    }
  }
  const stampsByAnchor = new Map<number, CompiledStamp[]>();
  for (const st of stamps) {
    let l = stampsByAnchor.get(st.anchor);
    if (!l) stampsByAnchor.set(st.anchor, (l = []));
    l.push(st);
  }
  const cases = { dirt: new Map<string, Dist>(), grass: new Map<string, Dist>() };
  for (const layer of ['dirt', 'grass'] as const)
    for (const [k, arr] of Object.entries(ov?.cases?.[layer] ?? {})) {
      const d = dist(arr);
      if (d) cases[layer].set(k, d);
    }
  const anyExact = (m: Map<string, Dist>) => [...m.keys()].some(isExactCase);
  const anyTone = (m: Map<string, Dist>) => [...m.keys()].some(isToneCase);
  const darkSprites = new Set<number>();
  for (const c of fill.c) if (c >= 0) darkSprites.add(c >> 3);
  for (const t of tones) if (t.c >= 0) darkSprites.add(t.c >> 3);
  return {
    cases,
    hasExact: { dirt: anyExact(cases.dirt), grass: anyExact(cases.grass) },
    hasTone: { dirt: anyTone(cases.dirt), grass: anyTone(cases.grass) },
    darkSprites,
    manualOnly: !!ov?.cases?.manualOnly,
    mirror: !!ov?.cases?.mirror,
    data,
    sprites,
    stampsByAnchor,
    directions: ov?.directions ?? ALL_DIRECTIONS,
    rules,
    ends,
    bigram: { level: compileBigram(data.level), fg: compileBigram(data.fg) },
    fill,
    tones,
    toneRules,
    lightTones,
    stamps,
    byShape,
    defaultShading: ov?.defaultShading ?? data.defaultShading,
    defaultDecoration: ov?.defaultDecoration ?? data.defaultDecoration,
  };
}

// ------------------------------------------------------------------ random

function hash(a: number, b: number, c: number, d: number): number {
  let h = Math.imul(a | 0, 0x27d4eb2d) ^ Math.imul(b | 0, 0x165667b1) ^ Math.imul(c | 0, 0x9e3779b1) ^ Math.imul(d | 0, 0x85ebca77);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

/** smooth value noise in [-1, 1] */
function valueNoise(x: number, y: number, seed: number, scale: number): number {
  const fx = x / scale, fy = y / scale;
  const ix = Math.floor(fx), iy = Math.floor(fy);
  const tx = fx - ix, ty = fy - iy;
  const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
  const v = (i: number, j: number) => hash(ix + i, iy + j, seed, 77) * 2 - 1;
  const a = v(0, 0) + (v(1, 0) - v(0, 0)) * sx;
  const b = v(0, 1) + (v(1, 1) - v(0, 1)) * sx;
  return a + (b - a) * sy;
}

// ------------------------------------------------------------------ choice

const ALPHA = 1;

const MAX_RATIO = 20;

/** How much more likely `c` is next to `a` than on average (smoothed, capped). */
function ratio(b: Bigram, a: number | undefined, c: number, horizontal: boolean): number {
  // choices without statistics (mirrored sprites, user additions) are neutral
  if (a === undefined || a === FILL || c === FILL || !b.p.has(c)) return 1;
  const tot = (horizontal ? b.hTot : b.vTot).get(a);
  if (!tot) return 1;
  const p = Math.max(b.p.get(c) ?? 0, 1e-3);
  const n = (horizontal ? b.h : b.v).get(a)?.get(c) ?? 0;
  return Math.min(MAX_RATIO, (n + ALPHA * p) / (tot + ALPHA) / p);
}

const tmpW: number[] = [];
function sample(d: Dist, b: Bigram | null, left: number | undefined, below: number | undefined, r: number): number {
  let total = 0;
  for (let i = 0; i < d.c.length; i++) {
    let w = d.w[i];
    if (b) w *= ratio(b, left, d.c[i], true) * ratio(b, below, d.c[i], false);
    tmpW[i] = w;
    total += w;
  }
  let t = r * total;
  for (let i = 0; i < d.c.length; i++) {
    t -= tmpW[i];
    if (t < 0) return d.c[i];
  }
  return d.c[d.c.length - 1];
}

/** like sample(), weighing each choice against the neighbours on all four sides */
function sampleAround(d: Dist, b: Bigram, left: number | undefined, right: number | undefined, below: number | undefined, above: number | undefined, r: number): number {
  let total = 0;
  for (let i = 0; i < d.c.length; i++) {
    const c = d.c[i];
    const w =
      d.w[i] *
      ratio(b, left, c, true) *
      (right === undefined ? 1 : ratio(b, c, right, true)) *
      ratio(b, below, c, false) *
      (above === undefined ? 1 : ratio(b, c, above, false));
    tmpW[i] = w;
    total += w;
  }
  let t = r * total;
  for (let i = 0; i < d.c.length; i++) {
    t -= tmpW[i];
    if (t < 0) return d.c[i];
  }
  return d.c[d.c.length - 1];
}

const noneFree = new WeakMap<Dist, Dist | null>();
/** the distribution without its "no edge art" choice (null when that is all it has) */
function withoutNone(d: Dist): Dist | null {
  let r = noneFree.get(d);
  if (r === undefined) {
    const c: number[] = [], w: number[] = [];
    let total = 0;
    d.c.forEach((ch, i) => {
      if (ch === NONE) return;
      c.push(ch);
      w.push(d.w[i]);
      total += d.w[i];
    });
    r = c.length ? { c, w, total } : null;
    noneFree.set(d, r);
  }
  return r;
}

/** context level of the surface kind: own shape + which sides are open (see contextKeys) */
const SURFACE_LEVEL = 3;
const fullCache = new WeakMap<Dist, Map<string, Dist>>();

/**
 * Edge art for "full" mode (keys from the last lookup()). Where the levels mostly left a spot bare (e.g. a
 * ceiling or wall cell next to a slope), the few pieces they did put there are small corner bits, which
 * would leave a gap in the run. So the pieces of the context are weighted by how common they are along
 * that kind of surface in general: the run piece continues up to the corner.
 */
function fullPieces(rules: Map<string, Dist>, keys: string[], mkeys: string[]): Dist | null {
  const spec = anyPiece(rules, keys, mkeys);
  if (!spec) return null;
  const sk = keys[SURFACE_LEVEL] + '|' + mkeys[SURFACE_LEVEL];
  let byKey = fullCache.get(spec);
  if (!byKey) fullCache.set(spec, (byKey = new Map()));
  let r = byKey.get(sk);
  if (!r) {
    const share = new Map<number, number>();
    const d = rules.get(keys[SURFACE_LEVEL]);
    const m = mkeys[SURFACE_LEVEL] === keys[SURFACE_LEVEL] ? undefined : rules.get(mkeys[SURFACE_LEVEL]);
    const dm = m && withoutNone(m);
    for (const x of [d && withoutNone(d), dm && mirrored(dm)]) {
      if (!x) continue;
      x.c.forEach((ch, i) => share.set(ch, (share.get(ch) ?? 0) + x.w[i] / x.total));
    }
    const c: number[] = [], w: number[] = [];
    let total = 0;
    spec.c.forEach((ch, i) => {
      const s = share.get(ch);
      if (!s) return;
      c.push(ch);
      w.push(spec.w[i] * s);
      total += spec.w[i] * s;
    });
    r = c.length ? { c, w, total } : spec;
    byKey.set(sk, r);
  }
  return r;
}

/**
 * The most specific context (keys from the last lookup()) in which the levels put edge art often enough
 * to trust, as lookup() does for whole contexts: a context that is nearly always bare keeps only its odd
 * one-off pieces once "none" is dropped, so it falls through to a vaguer one. Without enough evidence
 * anywhere: any piece at all.
 */
function anyPiece(rules: Map<string, Dist>, keys: string[], mkeys: string[]): Dist | null {
  for (const strict of [true, false]) {
    for (let l = 0; l < KEY_LEVELS; l++) {
      const need = strict ? MIN_COUNT[l] : 0;
      const d = rules.get(keys[l]);
      const r = d && withoutNone(d);
      const m = mkeys[l] === keys[l] ? undefined : rules.get(mkeys[l]);
      const rm = m && withoutNone(m);
      if (rm && rm.total >= need && rm.total > 4 * (r?.total ?? 0)) return mirrored(rm);
      if (r && r.total >= need) return r;
      if (rm && rm.total >= need) return mirrored(rm);
    }
  }
  return null;
}

/** side of a cell its bare neighbour is on */
type Side = 'L' | 'R' | 'B' | 'T';
const MIRROR_SIDE: Record<Side, Side> = { L: 'R', R: 'L', B: 'B', T: 'T' };

/**
 * Pieces for a run end: what the levels put at the end of a run on this kind of surface (level-3 context,
 * or its mirror image) beside a bare neighbour on that side, where it is more typical than in the middle
 * of a run (the plain run piece also ends runs abruptly). With bare neighbours on several sides, pieces
 * seen for all of them are preferred.
 */
function endCaps(st: CompiledStyle, ctx: Dist | null, keys: string[], mkeys: string[], sides: Side[]): Dist | null {
  // a piece and its mirror image count as the same piece
  const flip = (c: number) => (c & ~7) | composeFlags(c & 7, FLIP_X);
  const share = new Map<number, number>();
  if (ctx)
    ctx.c.forEach((c, i) => {
      if (c < 0) return;
      share.set(c, (share.get(c) ?? 0) + ctx.w[i] / ctx.total);
      share.set(flip(c), (share.get(flip(c)) ?? 0) + ctx.w[i] / ctx.total);
    });
  const per = sides.map((side) => {
    const m = new Map<number, number>();
    const e = st.ends.get(keys[3] + ':' + side);
    const me = mkeys[3] === keys[3] && MIRROR_SIDE[side] === side ? undefined : st.ends.get(mkeys[3] + ':' + MIRROR_SIDE[side]);
    for (const d of [e, me && mirrored(me)]) {
      if (!d) continue;
      d.c.forEach((c, i) => {
        if (c < 0 || (d.w[i] < 2 && d.total >= 6)) return;
        if (d.w[i] / d.total <= 1.2 * Math.max(share.get(c) ?? 0, 0.02)) return;
        m.set(c, (m.get(c) ?? 0) + d.w[i]);
      });
    }
    return m;
  });
  for (const all of [true, false]) {
    const c: number[] = [], w: number[] = [];
    let total = 0;
    const cand = new Set(per.flatMap((m) => [...m.keys()]));
    for (const ch of cand) {
      const n = per.map((m) => m.get(ch) ?? 0);
      const score = all ? Math.min(...n) : Math.max(...n);
      if (score <= 0) continue;
      c.push(ch);
      w.push(score);
      total += score;
    }
    if (c.length) return { c, w, total };
  }
  return null;
}

let lastLookup = '';

/** neighbour index of the horizontally mirrored neighbourhood */
const MIRROR_NB = [1, 0, 2, 3, 5, 4, 7, 6];
const FLIP_X = 1;
const mirrorNb = new Array<number>(8);
const mirrorCache = new WeakMap<Dist, Dist>();

function mirrored(d: Dist): Dist {
  let m = mirrorCache.get(d);
  if (!m) {
    m = { c: d.c.map((ch) => (ch < 0 ? ch : (ch & ~7) | composeFlags(ch & 7, FLIP_X))), w: d.w, total: d.total };
    mirrorCache.set(d, m);
  }
  return m;
}

/**
 * Most specific learned distribution for a context. When a context was not seen often enough, its
 * mirror image (flipped sprites) at the same specificity is preferred over a vaguer context: the levels
 * rarely contain every slope and wall in both orientations.
 */
function lookup(rules: Map<string, Dist>, own: number, nb: number[], keys: string[], mkeys: string[]): Dist | null {
  contextKeys(own, nb, keys);
  for (let i = 0; i < 8; i++) mirrorNb[i] = transformShape(nb[MIRROR_NB[i]], FLIP_X);
  contextKeys(transformShape(own, FLIP_X), mirrorNb, mkeys);
  let first: Dist | null = null;
  lastLookup = 'weak';
  for (let l = 0; l < KEY_LEVELS; l++) {
    const d = rules.get(keys[l]);
    const m = mkeys[l] === keys[l] ? undefined : rules.get(mkeys[l]);
    // a handful of odd observations must not outvote a well-established mirror image
    if (m && m.total >= MIN_COUNT[l] && m.total > 4 * (d?.total ?? 0)) {
      lastLookup = mkeys[l] + '~';
      return mirrored(m);
    }
    if (d && d.total >= MIN_COUNT[l]) {
      lastLookup = keys[l];
      return d;
    }
    if (!first) first = d ?? (m ? mirrored(m) : null);
  }
  return first;
}

/** The entry of a case, else of its general case (inside corners with any shape of air around them). */
function caseEntry(m: Map<string, Dist>, key: string): { d: Dist; general: boolean } | null {
  const d = m.get(key);
  if (d) return { d, general: false };
  const g = generalCase(key);
  const gd = g ? m.get(g) : undefined;
  return gd ? { d: gd, general: true } : null;
}

type LinkedEntry = { d: Dist; general: boolean; mirrored: boolean; exact: boolean };

/**
 * Hand-linked choices of a tile case: with `nb`, first the case narrowed to exactly these neighbours
 * (see exactCase); then its own (or general case's) entry, else (style option) its mirror image's flipped.
 */
function linkedEntry(st: CompiledStyle, layer: CaseLayer, key: string, nb?: ArrayLike<number>): LinkedEntry | null {
  const m = st.cases[layer];
  if (!key || !m.size) return null;
  if (nb && st.hasExact[layer]) {
    const xk = exactCase(key, nb);
    const xd = m.get(xk);
    if (xd) return { d: xd, general: false, mirrored: false, exact: true };
    const mxk = st.mirror ? mirrorCase(xk) : xk;
    const mxd = mxk === xk ? undefined : m.get(mxk);
    if (mxd) return { d: mirrored(mxd), general: false, mirrored: true, exact: true };
  }
  const e = caseEntry(m, key);
  if (e || !st.mirror) return e && { ...e, mirrored: false, exact: false };
  const mk = mirrorCase(key);
  const me = mk === key ? null : caseEntry(m, mk);
  return me && { d: mirrored(me.d), general: me.general, mirrored: true, exact: false };
}

const linkedCase = (st: CompiledStyle, layer: CaseLayer, key: string, nb?: ArrayLike<number>): Dist | null => linkedEntry(st, layer, key, nb)?.d ?? null;

/** The light/dark link for this neighbourhood (see toneCase), else null. It does not fall back to the case. */
function linkedTone(st: CompiledStyle, key: string, nb: number[], mask: number): Dist | null {
  if (!key || !st.hasTone.dirt) return null;
  const m = st.cases.dirt;
  const tk = toneCase(key, nb, mask);
  const td = m.get(tk);
  if (td) return td;
  if (!st.mirror) return null;
  const mk = mirrorCase(tk);
  const md = mk === tk ? undefined : m.get(mk);
  return md ? mirrored(md) : null;
}

/** Linked choices of a case as resolved, flat [choice, weight, ...] (null: not linked). `nb`: see linkedEntry. */
export function linkedChoices(st: CompiledStyle, layer: CaseLayer, key: string, nb?: ArrayLike<number>): { arr: number[]; mirrored: boolean; general: boolean; exact: boolean } | null {
  const e = linkedEntry(st, layer, key, nb);
  if (!e) return null;
  return { arr: e.d.c.flatMap((c, i) => [c, e.d.w[i]]), mirrored: e.mirrored, general: e.general, exact: e.exact };
}

/** What the learned rules put in a cell with this neighbourhood (auto edge mode), flat [choice, weight, ...]. */
export function learnedChoices(st: CompiledStyle, layer: CaseLayer, own: number, nb: number[]): number[] {
  const keys: string[] = [], mkeys: string[] = [];
  if (layer === 'dirt') {
    if (isInterior(own, nb)) return [FILL, 1];
    const d = lookupLevel(st.rules.level, own, nb, keys, mkeys);
    if (d) return d.c.flatMap((c, i) => [c, d.w[i]]);
    const b = own === FULL ? FILL : st.byShape.get(own);
    return b === undefined ? [] : [b, 1];
  }
  const d = lookup(st.rules.fg, own, nb, keys, mkeys);
  return d ? d.c.flatMap((c, i) => [c, d.w[i]]) : [];
}

const nearNb = new Array<number>(8);

/** Does the air of a diagonal neighbour (NB index 4..7) reach the corner point it shares with this cell? */
function airAtCorner(shape: number, i: number): boolean {
  const [dx, dy] = NB[i];
  const cx = dx < 0 ? 1 : 0, cy = dy < 0 ? 1 : 0;
  const sx = cx ? -1 : 1, sy = cy ? -1 : 1;
  const e = 0.02;
  return !insideShape(shape, cx + sx * e, cy + sy * 2 * e) || !insideShape(shape, cx + sx * 2 * e, cy + sy * e);
}
/**
 * lookup() for the level layer. Next to a slope squeezed into a narrow gap (e.g. the bottom of a
 * one-tile notch ending in a 45° slope) the neighbourhood never occurs in the levels, and the vague
 * fallback treats the cell as deep inside (plain fill): the edge band would stop at the slope. Then the
 * best-known neighbourhood with one diagonal neighbour opened up is used instead, which wraps the
 * band around the tip like at the foot of an ordinary slope.
 */
function lookupLevel(rules: Map<string, Dist>, own: number, nb: number[], keys: string[], mkeys: string[]): Dist | null {
  // A solid cell with no empty neighbour can still touch the air at one corner point: the end of a
  // squeezed slope's diagonal (beside the tip of a one-tile notch). The keys count that corner as solid
  // and pick plain fill, leaving a step in the band; it is looked up as the inside corner it really is.
  if (own === FULL && !nb.includes(EMPTY)) {
    for (let i = 4; i < 8; i++) {
      if (nb[i] === FULL || !airAtCorner(nb[i], i)) continue;
      for (let j = 0; j < 8; j++) nearNb[j] = nb[j];
      nearNb[i] = EMPTY;
      const v = lookup(rules, own, nearNb, keys, mkeys);
      if (v && (lastLookup[0] === 'a' || lastLookup[0] === 'b')) return v;
    }
  }
  const d = lookup(rules, own, nb, keys, mkeys);
  if (lastLookup[0] === 'a' || lastLookup[0] === 'b') return d;
  let slope = own !== FULL;
  for (let i = 0; i < 8 && !slope; i++) slope = nb[i] !== FULL && nb[i] !== EMPTY;
  if (!slope) return d;
  let best = -1, bestN = 0;
  for (let i = 4; i < 8; i++) {
    if (nb[i] === EMPTY) continue;
    for (let j = 0; j < 8; j++) nearNb[j] = nb[j];
    nearNb[i] = EMPTY;
    const v = lookup(rules, own, nearNb, keys, mkeys);
    if (v && lastLookup[0] === 'a' && v.total > bestN) {
      best = i;
      bestN = v.total;
    }
  }
  if (best < 0) return lookup(rules, own, nb, keys, mkeys);
  for (let j = 0; j < 8; j++) nearNb[j] = nb[j];
  nearNb[best] = EMPTY;
  return lookup(rules, own, nearNb, keys, mkeys);
}

// ------------------------------------------------------------------ generation

export interface GroupInput {
  style: CompiledStyle;
  variant: number;
  shapes: { shape: TerrainShape; paintIndex: number }[];
  targets: { level: string; foreground?: string; background?: string };
}

export interface GenerateOutput {
  /** layer id -> [x, y, styleSpriteChoice...] resolved to palette entries by the caller */
  tiles: Map<string, { pe: PaletteEntry; x: number; y: number; f: number }[]>;
  problems: { x: number; y: number; message: string }[];
  missing: MissingTile[];
  /** surface cells that can carry grass: flat [x, y, own shape, exposed LINE_* bits, exposed halves (exposedHalves)] */
  surface: number[];
  /** style id of each surface cell (one per surface entry) */
  surfaceStyle: string[];
}

/** A cell on a slope the style's art has no dirt / grass piece for (and no hand-linked one). */
export interface MissingTile {
  x: number;
  y: number;
  style: string;
  dirt: boolean;
  grass: boolean;
}

const S_DEFAULT = 4;

/**
 * Paint all shapes of a container into one owner grid (later shapes override earlier ones, subtract
 * shapes erase) and generate the tiles of each style group.
 */
export function generateContainer(
  shapes: TerrainShape[],
  groupOf: (t: TerrainShape) => { key: string; style: CompiledStyle; variant: number } | null,
  S = S_DEFAULT,
  grassPaint?: GrassPaint,
  decoPaint?: FoliagePaint,
  grassLines?: GrassLines,
): GenerateOutput {
  const out: GenerateOutput = { tiles: new Map(), problems: [], missing: [], surface: [], surfaceStyle: [] };
  const visible = shapes.filter((t) => !t.hidden && t.rings.length && t.rings[0].length >= 3);
  if (!visible.length) return out;
  let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
  for (const t of visible) {
    if (t.mode !== 'add') continue;
    const [a, b, c, d] = ringsBounds(t.rings);
    bx0 = Math.min(bx0, a); by0 = Math.min(by0, b); bx1 = Math.max(bx1, c); by1 = Math.max(by1, d);
  }
  if (!isFinite(bx0)) return out;
  const M = 6;
  const x0 = Math.floor(bx0) - M, y0 = Math.floor(by0) - M;
  const w = Math.ceil(bx1) - x0 + M, h = Math.ceil(by1) - y0 + M;
  if (w * h > 4_000_000) return out;
  const grid = createGrid(x0, y0, w, h, S);
  // paint
  const groups = new Map<string, { key: string; style: CompiledStyle; variant: number; paint: Set<number>; shapes: TerrainShape[]; targets: TerrainShape['targets'] }>();
  const paintGroup: number[] = [];
  visible.forEach((t, i) => {
    if (t.mode === 'subtract') {
      paintRings(grid, t.rings, -1);
      paintGroup.push(-1);
      return;
    }
    const g = groupOf(t);
    if (!g) {
      paintGroup.push(-1);
      return;
    }
    const key = g.key;
    let gr = groups.get(key);
    if (!gr) groups.set(key, (gr = { key, style: g.style, variant: g.variant, paint: new Set(), shapes: [], targets: t.targets }));
    gr.paint.add(i);
    gr.shapes.push(t);
    paintRings(grid, t.rings, i);
  });
  let painted: Map<number, number> | null = null;
  if (grassPaint?.length) {
    painted = new Map();
    for (let i = 0; i + 2 < grassPaint.length; i += 3) painted.set(cellKey(grassPaint[i], grassPaint[i + 1]), grassPaint[i + 2]);
  }
  let foliage: Map<number, number> | null = null;
  if (decoPaint?.length) {
    foliage = new Map();
    for (let i = 0; i + 2 < decoPaint.length; i += 3) foliage.set(cellKey(decoPaint[i], decoPaint[i + 1]), decoPaint[i + 2]);
  }
  let lineMarks: Map<number, number> | null = null;
  if (grassLines?.length) {
    lineMarks = new Map();
    for (let i = 0; i + 2 < grassLines.length; i += 3) if (grassLines[i + 2]) lineMarks.set(cellKey(grassLines[i], grassLines[i + 1]), grassLines[i + 2]);
  }
  for (const gr of groups.values()) generateGroup(grid, gr, visible, out, painted, foliage, lineMarks);
  return out;
}

export const cellKey = (x: number, y: number) => (x + 32768) * 65536 + (y + 32768);

/** What generation decided for one terrain cell (probeContainer). */
export interface CellProbe {
  /** style group key passed by groupOf */
  group: string;
  style: CompiledStyle;
  variant: number;
  x: number;
  y: number;
  own: number;
  /** neighbour shapes in NB order */
  nb: number[];
  /** shapes 2 cells out (cases.ts NB2 order) */
  ring: number[];
  /** 1 where that neighbour shows light ground: NB order, then NB2 (see toneCase) */
  light: number[];
  /** final level-layer choice (NONE: nothing) */
  level: number;
  /** final foreground choice (NONE: nothing) */
  fg: number;
  hasForeground: boolean;
  /** surface cell that can carry edge art */
  edge: boolean;
  /** edge art mode at the cell (grass paint over the shape's setting) */
  edgeMode: EdgeArtMode;
  /** the cell's grass case, then the cases it takes hand-linked tiles from when it has none of its own */
  grassKeys: string[];
  /** the shape and neighbourhood the learned grass pieces are chosen for (differs from own / nb when lines are bare) */
  grassOwn: number;
  grassNb: number[];
}

/** straight lines and the direction their run continues in: floors and ceilings along x, walls along y */
const LINE_LIST = [LINE_L, LINE_R, LINE_B, LINE_T, LINE_S];

/** How a cell with marked grass lines (or next to them) chooses its grass. */
interface LineView {
  own: number;
  nb: number[];
  mode: EdgeArtMode;
  /** lines drawn with grass: the grassy ones and those ending a run beside them */
  grassy: number;
  bare: number;
  marked: number;
  /** sides a run beside the cell ends at */
  ends: Side[];
  /** the cell's only grass is the end of a run along a slope: no learned end piece leaves it bare */
  slopeEnd: boolean;
  /** its own grass case first (partly grassy: partialCase), then the cases it falls back to */
  keys: string[];
}

let probe: { x: number; y: number; res: CellProbe | null } | null = null;

/** Generate a container and report what its terrain does at cell (x, y); null when no terrain covers it. */
export function probeContainer(
  shapes: TerrainShape[],
  groupOf: (t: TerrainShape) => { key: string; style: CompiledStyle; variant: number } | null,
  x: number,
  y: number,
  S = S_DEFAULT,
  grassPaint?: GrassPaint,
  decoPaint?: FoliagePaint,
  grassLines?: GrassLines,
): CellProbe | null {
  probe = { x, y, res: null };
  try {
    generateContainer(shapes, groupOf, S, grassPaint, decoPaint, grassLines);
    return probe.res;
  } finally {
    probe = null;
  }
}

function generateGroup(
  g: SampleGrid,
  gr: { key: string; style: CompiledStyle; variant: number; paint: Set<number>; targets: TerrainShape['targets'] },
  shapes: TerrainShape[],
  out: GenerateOutput,
  painted: Map<number, number> | null,
  foliage: Map<number, number> | null,
  lineMarks: Map<number, number> | null,
) {
  const st = gr.style;
  const { w, h, S, x0, y0 } = g;
  const mine = (o: number) => o >= 0 && gr.paint.has(o);
  const cls = classifyCells(g, mine, (id) => shapeKindAllowed(id, st.directions));
  const shape = cls.shape;
  for (const ci of cls.bad) {
    const sh = shape[ci];
    out.problems.push({ x: x0 + (ci % w) + 0.5, y: y0 + Math.floor(ci / w) + 0.5, message: sh === EMPTY || sh === FULL ? 'Cell geometry not supported by this style' : `Cell geometry not supported (used ${SHAPES[sh].kind})` });
  }
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= w || y >= h ? EMPTY : shape[y * w + x]);
  // owner shape per cell (for settings): owner of a central sample, propagated to empty cells
  const W = w * S;
  const ownerCell = new Int16Array(w * h).fill(-1);
  const mid = S >> 1;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      if (shape[y * w + x] === EMPTY) continue;
      let o = -1;
      for (let b = 0; b < S && o < 0; b++)
        for (let a = 0; a < S && o < 0; a++) {
          const v = g.owner[(y * S + ((b + mid) % S)) * W + x * S + ((a + mid) % S)];
          if (mine(v)) o = v;
        }
      ownerCell[y * w + x] = o;
    }
  // one ring per pass, read from the previous pass, so an empty cell belongs to the nearest polygon (not
  // to whichever one the scan reached first)
  for (let pass = 0; pass < 3; pass++) {
    const prev = ownerCell.slice();
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        if (prev[y * w + x] >= 0) continue;
        for (let i = 0; i < 8; i++) {
          const nx = x + NB[i][0], ny = y + NB[i][1];
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          const o = prev[ny * w + nx];
          if (o >= 0) {
            ownerCell[y * w + x] = o;
            break;
          }
        }
      }
  }
  const shapeOf = (ci: number) => shapes[ownerCell[ci]] ?? shapes[[...gr.paint][0]];
  const decoSeed = (ci: number) => shapeOf(ci).decoration?.seed ?? 1;

  // ---- level layer
  const lvl = new Int32Array(w * h).fill(NONE);
  const isFill = new Uint8Array(w * h);
  const keys: string[] = [];
  const mkeys: string[] = [];
  const nb = new Array<number>(8);
  const edge = new Uint8Array(w * h);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const ci = y * w + x;
      const own = shape[ci];
      if (own === EMPTY) continue;
      for (let i = 0; i < 8; i++) nb[i] = at(x + NB[i][0], y + NB[i][1]);
      if (isEdgeZone(own, nb)) edge[ci] = 1;
      const seed = decoSeed(ci);
      const left = x > 0 && lvl[ci - 1] >= 0 ? lvl[ci - 1] : undefined;
      const below = y > 0 && lvl[ci - w] >= 0 ? lvl[ci - w] : undefined;
      let ch: number;
      const interior = isInterior(own, nb);
      let d: Dist | null = null;
      let linked = false;
      if (st.cases.dirt.size || st.manualOnly) {
        const k = interior ? '' : caseKey('dirt', own, nb);
        if (k) d = linkedCase(st, 'dirt', k, nb);
        else if (own === FULL && st.hasExact.dirt) {
          const bn = buriedNeighbours(nb, () => NB2.map(([dx, dy]) => at(x + dx, y + dy)));
          if (bn) d = linkedCase(st, 'dirt', BURIED, bn);
        }
        linked = !!d;
      }
      if (!interior && !linked && !st.manualOnly) d = lookupLevel(st.rules.level, own, nb, keys, mkeys);
      if (d) ch = sample(d, st.bigram.level, left, below, hash(x + x0, y + y0, seed, 11));
      else if (own === FULL) ch = FILL;
      else ch = st.byShape.get(own) ?? FILL;
      if (ch === FILL) {
        isFill[ci] = 1;
        ch = st.fill.c.length ? sample(st.fill, st.bigram.level, left, below, hash(x + x0, y + y0, seed, 13)) : (st.byShape.get(FULL) ?? NONE);
      }
      lvl[ci] = ch;
      if (d) debugHooks.rule?.('level', x + x0, y + y0, linked ? 'linked' : lastLookup, ch);
    }

  // ---- dark shading
  if (st.tones.length) applyShading(g, gr, shapes, ownerCell, isFill, lvl, mine);

  // ---- light ground touching light ground
  // A neighbour counts as light when the tile it shows is not plain fill (nor a dark shading tone). That
  // is read after the case links and exact-neighbour links, so fixing a cell there changes what the cells
  // next to it see. The light/dark link is then painted on top, and does not change those readings: a row
  // of the same pattern stays linked instead of each cell flipping its neighbours.
  const shownLight = new Uint8Array(w * h);
  const markLight = (src: Int32Array) => {
    shownLight.fill(0);
    for (let ci = 0; ci < src.length; ci++) {
      const ch = src[ci];
      if (ch >= 0 && !st.darkSprites.has(ch >> 3)) shownLight[ci] = 1;
    }
  };
  const applyTone = () => {
    const coords8 = NB;
    const coords24 = [...NB, ...NB2];
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const ci = y * w + x;
        const own = shape[ci];
        if (own === EMPTY) continue;
        for (let i = 0; i < 8; i++) nb[i] = at(x + NB[i][0], y + NB[i][1]);
        const interior = isInterior(own, nb);
        let k = '';
        let cells: number[] | null = null;
        if (!interior) k = caseKey('dirt', own, nb);
        if (!interior && k) cells = nb.slice();
        else if (own === FULL) {
          cells = buriedNeighbours(nb, () => NB2.map(([dx, dy]) => at(x + dx, y + dy)));
          if (cells) k = BURIED;
        }
        if (!k || !cells) continue;
        const coords = cells.length > 8 ? coords24 : coords8;
        let mask = 0;
        for (let i = 0; i < cells.length; i++) {
          const nx = x + coords[i][0], ny = y + coords[i][1];
          if (nx >= 0 && ny >= 0 && nx < w && ny < h && shownLight[ny * w + nx]) mask |= 1 << i;
        }
        const d = linkedTone(st, k, cells, mask);
        if (!d) continue;
        const seed = decoSeed(ci);
        const left = x > 0 && lvl[ci - 1] >= 0 ? lvl[ci - 1] : undefined;
        const below = y > 0 && lvl[ci - w] >= 0 ? lvl[ci - w] : undefined;
        let ch = sample(d, st.bigram.level, left, below, hash(x + x0, y + y0, seed, 17));
        if (ch === FILL) {
          isFill[ci] = 1;
          ch = st.fill.c.length ? sample(st.fill, st.bigram.level, left, below, hash(x + x0, y + y0, seed, 19)) : (st.byShape.get(FULL) ?? NONE);
        } else isFill[ci] = ch >= 0 && st.darkSprites.has(ch >> 3) ? 1 : 0;
        lvl[ci] = ch;
      }
  };
  if (st.hasTone.dirt || probe) {
    markLight(lvl);
    if (st.hasTone.dirt) applyTone();
  }

  // ---- outline lines that face air, and per-line grass
  const lines = new Uint8Array(w * h);
  const sideHalves = new Uint8Array(w * h);
  for (let ci = 0; ci < w * h; ci++) {
    if (!edge[ci]) continue;
    const x = ci % w, y = (ci - x) / w;
    for (let i = 0; i < 8; i++) nb[i] = at(x + NB[i][0], y + NB[i][1]);
    lines[ci] = exposedLines(shape[ci], nb);
    sideHalves[ci] = exposedHalves(shape[ci], nb);
    if (lines[ci] && gr.targets.foreground) {
      out.surface.push(x0 + x, y0 + y, shape[ci], lines[ci], sideHalves[ci]);
      out.surfaceStyle.push(st.data.id);
    }
  }
  /** end points of a cell's line (cell coordinates): lower / left end first */
  const lineEnds = (ci: number, bit: number): [[number, number], [number, number]] | null => {
    if (bit === LINE_S) {
      const seg = slopeSegment(shape[ci]);
      return seg && (seg[0][0] > seg[1][0] ? [seg[1], seg[0]] : seg);
    }
    const s = Math.log2(bit), hb = (sideHalves[ci] >> (2 * s)) & 3;
    const t0 = hb & 1 ? 0 : 0.5, t1 = hb & 2 ? 1 : 0.5;
    const pt = (t: number): [number, number] => (s === 0 ? [0, t] : s === 1 ? [1, t] : s === 2 ? [t, 0] : [t, 1]);
    return [pt(t0), pt(t1)];
  };
  const cellMode = (ci: number, usePaint: boolean): EdgeArtMode => {
    const p = usePaint ? painted?.get(cellKey(x0 + (ci % w), y0 + Math.floor(ci / w))) : undefined;
    if (p !== undefined) return p ? 'full' : 'none';
    return shapeOf(ci).decoration?.edge ?? 'auto';
  };
  /*
   * Cells with marked lines choose their grass in a view of the terrain where only the grassy lines are open
   * (grassView): a corner with grass on top only gets the pieces of a straight run, ended like a run. A
   * partly grassy cell (also an inside corner where only one of the surfaces meeting there is grassy) has a
   * grass case of its own (partialCase), so it can be given its own tile; without one it uses the view's.
   */
  const lineViews = (marksIn: Map<number, number>, mode: (ci: number, usePaint: boolean) => EdgeArtMode) => {
    const marks = new Int32Array(w * h);
    let any = false;
    for (const [k, v] of marksIn) {
      const x = Math.floor(k / 65536) - 32768 - x0, y = (k % 65536) - 32768 - y0;
      if (x < 0 || y < 0 || x >= w || y >= h || !lines[y * w + x]) continue;
      marks[y * w + x] = v;
      any = true;
    }
    if (!any) return null;
    const bare = new Uint8Array(w * h);
    // grassy lines of every cell: marked grass, or the shape's grass setting where not marked bare
    const grassy = new Uint8Array(w * h);
    const marked = new Uint8Array(w * h);
    for (let ci = 0; ci < w * h; ci++) {
      if (!lines[ci]) continue;
      bare[ci] = (marks[ci] >> LINE_BITS) & lines[ci];
      marked[ci] = marks[ci] & ALL_LINES & lines[ci];
      grassy[ci] = marked[ci] | (mode(ci, true) !== 'none' ? lines[ci] & ~bare[ci] : 0);
    }
    // A run of grassy lines ends in the cell beside it: the line of another cell carrying on from one end of
    // the run (straight on, or round an outside corner), bare only by the shape's setting (not marked bare or
    // erased), gets the run's end piece, so the grassy lines keep whole pieces.
    const spill = new Uint8Array(w * h);
    // per line L, R, B, T, S the end the run comes in at (bit 0 lower / left, bit 1 upper / right)
    const from = new Uint16Array(w * h);
    // the same where the cell's case does not already tell it by the grass at an open corner there
    const halves = new Uint16Array(w * h);
    const PW = 4 * w + 1;
    const ptKey = (x: number, y: number, p: [number, number]) => Math.round((y + p[1]) * 4) * PW + Math.round((x + p[0]) * 4);
    const normalOf = (ci: number, bit: number): [number, number] => (bit === LINE_S ? SHAPES[shape[ci]].n : bit === LINE_L ? [-1, 0] : bit === LINE_R ? [1, 0] : bit === LINE_B ? [0, -1] : [0, 1]);
    const grassyEnds = new Map<number, [number, number][]>();
    for (let ci = 0; ci < w * h; ci++) {
      if (!grassy[ci]) continue;
      const x = ci % w, y = (ci - x) / w;
      for (const bit of LINE_LIST) {
        if (!(grassy[ci] & bit)) continue;
        for (const p of lineEnds(ci, bit) ?? []) {
          const k = ptKey(x, y, p);
          const list = grassyEnds.get(k);
          if (list) list.push([ci, bit]);
          else grassyEnds.set(k, [[ci, bit]]);
        }
      }
    }
    // a grassy line of another cell ends at p, and the line going on from p (direction d) is not round an
    // inside corner from it (the air there between the two)
    const runEndsAt = (ci: number, k: number, d: [number, number]) =>
      grassyEnds.get(k)?.some(([o, bit]) => {
        if (o === ci) return false;
        const n = normalOf(o, bit);
        return n[0] * d[0] + n[1] * d[1] < 1e-6;
      }) ?? false;
    for (let ci = 0; ci < w * h; ci++) {
      const E = lines[ci];
      if (!E || mode(ci, false) !== 'none' || mode(ci, true) !== 'none') continue;
      const x = ci % w, y = (ci - x) / w;
      let corners = -1;
      for (const bit of LINE_LIST) {
        if (!(E & bit) || (grassy[ci] | bare[ci]) & bit) continue;
        const ends = lineEnds(ci, bit);
        if (!ends) continue;
        const [a, b] = ends;
        const lo = runEndsAt(ci, ptKey(x, y, a), [b[0] - a[0], b[1] - a[1]]);
        if (lo === runEndsAt(ci, ptKey(x, y, b), [a[0] - b[0], a[1] - b[1]])) continue;
        spill[ci] |= bit;
        const end = lo ? 0 : 1, p = ends[end], shift = 2 * Math.log2(bit) + end;
        from[ci] |= 1 << shift;
        if (corners < 0) {
          for (let i = 0; i < 8; i++) nb[i] = at(x + NB[i][0], y + NB[i][1]);
          corners = insideCorners(shape[ci], nb);
        }
        const atCorner = (p[0] === 0 || p[0] === 1) && (p[1] === 0 || p[1] === 1) && (corners >> (p[0] + 2 * p[1])) & 1;
        if (!atCorner) halves[ci] |= 1 << shift;
      }
    }
    /** a run ends in cell ni with the end piece of one of these lines at point (px, py) */
    const spillsAt = (ni: number, bits: number, px: number, py: number) => {
      const nx = ni % w, ny = (ni - nx) / w;
      for (const bit of LINE_LIST) {
        if (!(bits & spill[ni] & bit)) continue;
        const ends = lineEnds(ni, bit);
        if (!ends) continue;
        for (let e = 0; e < 2; e++)
          if ((from[ni] >> (2 * Math.log2(bit) + e)) & 1 && ptKey(nx, ny, ends[e]) === ptKey(px, py, [0, 0])) return true;
      }
      return false;
    };
    const at2 = new Map<number, LineView>();
    const states: number[] = [];
    for (let ci = 0; ci < w * h; ci++) {
      if (!edge[ci]) continue;
      const x = ci % w, y = (ci - x) / w;
      const own = shape[ci];
      for (let i = 0; i < 8; i++) nb[i] = at(x + NB[i][0], y + NB[i][1]);
      // inside corners: which of the two surfaces meeting at the point are grassy
      const corners = insideCorners(own, nb);
      // joined: mixed corners whose other surface takes the end piece of the run there, so the
      // corner's grass carries on round it
      let drop = 0, mixed = 0, joined = 0, cornerOn = false;
      states.length = 0;
      for (let c = 0; c < 4; c++) {
        if (!((corners >> c) & 1)) continue;
        let st = 0, ends = 0;
        cornerSurfaces(c, own, nb)?.forEach((s, j) => {
          const nx = s.cell < 0 ? x : x + NB[s.cell][0], ny = s.cell < 0 ? y : y + NB[s.cell][1];
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) return;
          const ni = ny * w + nx;
          const line = s.line & lines[ni] || linesAtCorner(shape[ni], s.corner) & lines[ni];
          if (grassy[ni] & line) {
            st |= 1 << j;
            if (marked[ni] & line) cornerOn = true;
          } else if (ni !== ci && spillsAt(ni, line, x + (c & 1), y + (c >> 1))) ends |= 1 << j;
        });
        states.push(st);
        if (!st) drop |= 1 << c;
        else if (st !== 3) {
          mixed |= 1 << c;
          if ((st | ends) === 3) joined |= 1 << c;
        }
      }
      const E = lines[ci], sp = spill[ci], g = grassy[ci] & E, drawn = g | sp;
      const v = marks[ci];
      const partial = g !== E || !!drop || !!mixed;
      const base = mode(ci, true);
      if (!v && !cornerOn && !sp && !(partial && base !== 'none')) continue;
      const on = (v & ALL_LINES & E) | sp;
      const cut = E & ~drawn;
      const kept = corners & ~drop;
      // a cell whose own lines are all bare can still carry the grass of an inside corner with a marked line
      const m: EdgeArtMode = drawn ? (on ? 'full' : base) : !kept ? 'none' : cornerOn ? 'full' : E ? 'none' : base;
      // a corner-only cell where one surface is grassy takes a hand-linked tile only (the learned
      // pieces of a corner wrap grass round both surfaces)
      const learnDrop = drawn ? drop : drop | (mixed & ~joined);
      const vnb = [...nb];
      const vown = cut || learnDrop ? grassView(own, nb, cut, learnDrop, vnb) : own;
      const keys: string[] = [];
      if (partial) keys.push(partialCase(caseKey('grass', own, nb), g, states, halves[ci]));
      if (!drawn && mixed) {
        const onb = [...nb];
        keys.push(caseKey('grass', grassView(own, nb, cut, drop, onb), onb));
      }
      // the run's end piece is not the whole-line grass linked to the view's case: the learned end pieces
      // stand in for it until one is linked, where the art has pieces for the cell at all
      const kind = SHAPES[own].kind;
      const learned = !st.manualOnly && (kind === 'full' || (kind !== 'empty' && st.data.directions[kind]));
      if (!sp || !learned) keys.push(caseKey('grass', vown, vnb));
      const ends: Side[] = [];
      for (const bit of LINE_LIST) {
        if (!(sp & bit)) continue;
        const fromLo = (from[ci] >> (2 * Math.log2(bit))) & 1;
        ends.push(bit & (LINE_L | LINE_R) ? (fromLo ? 'T' : 'B') : fromLo ? 'R' : 'L');
      }
      at2.set(ci, { own: vown, nb: vnb, mode: m, grassy: drawn, bare: cut, marked: v & ALL_LINES & E, ends, slopeEnd: !g && !!(sp & LINE_S), keys });
    }
    return { at: at2, bare };
  };
  /** hand-linked grass of a cell with a view: its own case, else the cases it falls back to */
  const viewLink = (v: LineView): Dist | null => {
    for (let i = 0; i < v.keys.length; i++) {
      const d = linkedCase(st, 'grass', v.keys[i], i === 0 ? v.nb : undefined);
      if (d) return d;
    }
    return null;
  };
  const views = gr.targets.foreground && lineMarks?.size ? lineViews(lineMarks, cellMode) : null;
  /** marked-bare exposed lines per cell: surface decorations stay off them */
  const bareLines = views?.bare ?? null;

  // ---- foreground edge art
  const linkedFg = new Uint8Array(w * h);
  /** ref: the result without paint, which painted-bare cells show their neighbours instead of a gap */
  const edgeArt = (fg: Int32Array, usePaint: boolean, ref: Int32Array | null = null) => {
    const fgDist: (Dist | null)[] = new Array(w * h).fill(null);
    const useCases = st.cases.grass.size > 0 || st.manualOnly;
    // cells bare only because they are painted bare: neighbours carry on as if they had their unpainted
    // grass instead of continuing a learned gap (the run then gets its end piece below)
    const erased = new Uint8Array(w * h);
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const ci = y * w + x;
        if (!edge[ci]) continue;
        const view = usePaint ? views?.at.get(ci) : undefined;
        const mode = view ? view.mode : cellMode(ci, usePaint);
        if (mode === 'none') {
          if (usePaint && cellMode(ci, false) !== 'none') erased[ci] = 1;
          continue;
        }
        const own = view ? view.own : shape[ci];
        for (let i = 0; i < 8; i++) nb[i] = view ? view.nb[i] : at(x + NB[i][0], y + NB[i][1]);
        const lc = useCases ? (view ? viewLink(view) : linkedCase(st, 'grass', caseKey('grass', own, nb), nb)) : null;
        let d: Dist | null = null;
        if (lc) {
          linkedFg[ci] = 1;
          // "full" grass everywhere the art has a piece: a case linked to "nothing" falls back to the
          // learned surface pieces
          d = mode === 'full' ? withoutNone(lc) : lc;
        }
        if (!d && !st.manualOnly) {
          d = lookup(st.rules.fg, own, nb, keys, mkeys);
          if (mode === 'full' && (own !== FULL || hasOpenSide(nb)))
            d = fullPieces(st.rules.fg, keys, mkeys) ?? d;
          if (lc && d) d = withoutNone(d);
        }
        if (!d) continue;
        fgDist[ci] = d;
        const left = x > 0 && edge[ci - 1] ? (erased[ci - 1] ? ref?.[ci - 1] : fg[ci - 1]) : undefined;
        const below = y > 0 && edge[ci - w] ? (erased[ci - w] ? ref?.[ci - w] : fg[ci - w]) : undefined;
        fg[ci] = sample(d, st.bigram.fg, left, below, hash(x + x0, y + y0, decoSeed(ci), 17));
        debugHooks.rule?.('fg', x + x0, y + y0, lc ? 'linked' : lastLookup, fg[ci]);
      }
    // The scan above only sees the left / lower neighbour, so runs end on the right and at the top
    // with whatever came up. Re-choose every cell against all four neighbours: run ends get pieces
    // the levels use next to bare surface, and stray gaps / lone pieces inside runs resolve.
    const fgAt = (x: number, y: number) => (x < 0 || y < 0 || x >= w || y >= h || !edge[y * w + x] ? undefined : fg[y * w + x]);
    const ctxAt = (x: number, y: number) => (x < 0 || y < 0 || x >= w || y >= h ? undefined : erased[y * w + x] ? ref?.[y * w + x] : fgAt(x, y));
    for (let pass = 0; pass < 2; pass++)
      for (let k = 0; k < w * h; k++) {
        const ci = pass === 0 ? w * h - 1 - k : k;
        const d = fgDist[ci];
        if (!d) continue;
        const x = ci % w, y = (ci - x) / w;
        fg[ci] = sampleAround(d, st.bigram.fg, ctxAt(x - 1, y), ctxAt(x + 1, y), ctxAt(x, y - 1), ctxAt(x, y + 1), hash(x + x0, y + y0, decoSeed(ci), 41 + pass));
      }
    // Run ends: a piece next to a bare cell of the same surface (a gap, an erased or unpainted stretch)
    // only uses pieces the levels end a run with on that side, e.g. the tapered grass caps.
    const bare = (x: number, y: number) => fgAt(x, y) === NONE;
    const air = (x: number, y: number) => at(x, y) === EMPTY;
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const ci = y * w + x;
        // hand-linked pieces stay as linked
        if (fg[ci] < 0 || !fgDist[ci] || linkedFg[ci]) continue;
        let flat = air(x, y + 1) ? 1 : air(x, y - 1) ? -1 : 0;
        let wall = air(x + 1, y) ? 1 : air(x - 1, y) ? -1 : 0;
        // with lines marked bare the run is along the grassy lines only; where it turns a bare corner it
        // ends as the levels end grass at a corner (straight to the edge), so a bare line is no run end
        const view = usePaint ? views?.at.get(ci) : undefined;
        if (view?.bare) {
          const g = view.grassy, slope = g & LINE_S;
          flat = g & LINE_T ? 1 : g & LINE_B ? -1 : slope ? flat : 0;
          wall = g & LINE_R ? 1 : g & LINE_L ? -1 : slope ? wall : 0;
        }
        const ends: Side[] = view ? [...view.ends] : [];
        // a line marked grassy keeps its whole piece: its run ends in the cell beside it
        const whole = view?.marked ?? 0;
        const flatLine = flat > 0 ? LINE_T : LINE_B, wallLine = wall > 0 ? LINE_R : LINE_L;
        if (flat && !(whole & (lines[ci] & flatLine ? flatLine : LINE_S))) {
          if (bare(x - 1, y) && air(x - 1, y + flat)) ends.push('L');
          if (bare(x + 1, y) && air(x + 1, y + flat)) ends.push('R');
        }
        if (wall && !(whole & (lines[ci] & wallLine ? wallLine : LINE_S))) {
          if (bare(x, y - 1) && air(x + wall, y - 1)) ends.push('B');
          if (bare(x, y + 1) && air(x + wall, y + 1)) ends.push('T');
        }
        if (!ends.length) continue;
        for (let i = 0; i < 8; i++) nb[i] = view ? view.nb[i] : at(x + NB[i][0], y + NB[i][1]);
        const ctx = lookup(st.rules.fg, view ? view.own : shape[ci], nb, keys, mkeys);
        const caps = endCaps(st, ctx, keys, mkeys, ends);
        if (!caps) {
          if (view?.slopeEnd) fg[ci] = NONE;
          continue;
        }
        fg[ci] = sampleAround(caps, st.bigram.fg, fgAt(x - 1, y), fgAt(x + 1, y), fgAt(x, y - 1), fgAt(x, y + 1), hash(x + x0, y + y0, decoSeed(ci), 47));
      }
  };
  const fg = new Int32Array(w * h).fill(NONE);
  // painted grass is a layer over the generated result: decorations are placed as without it
  const auto = (painted || views) && gr.targets.foreground ? new Int32Array(w * h).fill(NONE) : fg;
  if (gr.targets.foreground) {
    edgeArt(auto, false);
    if (auto !== fg) edgeArt(fg, true, auto);
  }
  const bg = new Int32Array(w * h).fill(NONE);

  // ---- decoration stamps
  if (auto === fg) placeStamps(gr, shapes, w, h, x0, y0, shape, ownerCell, at, lvl, isFill, fg, bg, foliage, bareLines);
  else {
    const before = auto.slice();
    placeStamps(gr, shapes, w, h, x0, y0, shape, ownerCell, at, lvl, isFill, auto, bg, foliage, bareLines);
    for (let ci = 0; ci < auto.length; ci++) if (auto[ci] !== before[ci]) fg[ci] = auto[ci];
  }

  if (probe && !probe.res) {
    const px = probe.x - x0, py = probe.y - y0;
    const ci = py * w + px;
    if (px >= 0 && py >= 0 && px < w && py < h && shape[ci] !== EMPTY) {
      const p = painted?.get(cellKey(probe.x, probe.y));
      const view = views?.at.get(ci);
      probe.res = {
        group: gr.key,
        style: st,
        variant: gr.variant,
        x: probe.x,
        y: probe.y,
        own: shape[ci],
        nb: NB.map(([dx, dy]) => at(px + dx, py + dy)),
        ring: NB2.map(([dx, dy]) => at(px + dx, py + dy)),
        light: [...NB, ...NB2].map(([dx, dy]) => {
          const nx = px + dx, ny = py + dy;
          return nx >= 0 && ny >= 0 && nx < w && ny < h ? shownLight[ny * w + nx] : 0;
        }),
        level: lvl[ci],
        fg: fg[ci],
        hasForeground: !!gr.targets.foreground,
        edge: !!edge[ci],
        edgeMode: view ? view.mode : p !== undefined ? (p ? 'full' : 'none') : (shapeOf(ci).decoration?.edge ?? 'auto'),
        grassKeys: view ? [...view.keys] : [caseKey('grass', shape[ci], NB.map(([dx, dy]) => at(px + dx, py + dy)))],
        grassOwn: view ? view.own : shape[ci],
        grassNb: view ? [...view.nb] : NB.map(([dx, dy]) => at(px + dx, py + dy)),
      };
    }
  }

  // ---- slope cells the art has no piece for and nobody linked one to
  const artHas = st.data.directions;
  const hasGrass = !!gr.targets.foreground && (st.rules.fg.size > 0 || st.cases.grass.size > 0);
  for (let ci = 0; ci < w * h; ci++) {
    const own = shape[ci];
    const kind = SHAPES[own].kind;
    if (kind === 'empty' || kind === 'full' || artHas[kind]) continue;
    const x = ci % w, y = (ci - x) / w;
    for (let i = 0; i < 8; i++) nb[i] = at(x + NB[i][0], y + NB[i][1]);
    const dirt = !linkedCase(st, 'dirt', caseKey('dirt', own, nb), nb);
    let grass = false;
    if (hasGrass && edge[ci]) {
      const view = views?.at.get(ci);
      const mode = view ? view.mode : cellMode(ci, true);
      grass = mode !== 'none' && !(view ? viewLink(view) : linkedCase(st, 'grass', caseKey('grass', own, nb), nb));
    }
    if (dirt || grass) out.missing.push({ x: x0 + x, y: y0 + y, style: st.data.id, dirt, grass });
  }

  // ---- emit
  const vs = variantSprites(st, gr.variant);
  const emit = (layerId: string | undefined, arr: Int32Array) => {
    if (!layerId) return;
    let list = out.tiles.get(layerId);
    if (!list) out.tiles.set(layerId, (list = []));
    for (let ci = 0; ci < arr.length; ci++) {
      const ch = arr[ci];
      if (ch < 0) continue;
      const si = ch >> 3;
      const pe = vs[si] ?? st.sprites[si];
      if (!pe) continue;
      list.push({ pe, x: x0 + (ci % w), y: y0 + Math.floor(ci / w), f: ch & 7 });
    }
  };
  emit(gr.targets.level, lvl);
  emit(gr.targets.foreground, fg);
  emit(gr.targets.background, bg);
}

/**
 * Dark interior shading: the region deeper than the (noise-jittered) depth below the surface is
 * smoothed, classified into shape classes like the terrain itself and rendered with the tone tiles
 * through rules learned from the hand-shaded levels.
 */
function applyShading(
  g: SampleGrid,
  gr: { style: CompiledStyle },
  shapes: TerrainShape[],
  ownerCell: Int16Array,
  isFill: Uint8Array,
  lvl: Int32Array,
  mine: (o: number) => boolean,
) {
  const st = gr.style;
  const { w, h, S, x0, y0 } = g;
  const W = w * S;
  const d = sampleDistance(g, mine);
  // integer depth per cell keeps the offset boundary on the cell lattice (L1 offsets of 45° and 2:1
  // edges by whole cells stay exact shape classes); the noise only moves it in whole-cell steps
  const dg = createGrid(x0, y0, w, h, S);
  let any = false;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const ci = y * w + x;
      if (!isFill[ci]) continue;
      const sh = shapes[ownerCell[ci]]?.shading ?? st.defaultShading;
      if (!sh.enabled) continue;
      const thr = Math.max(2, Math.round(sh.depth + sh.jitter * 2.5 * valueNoise(x + x0, y + y0, sh.seed, 5))) * S;
      for (let b = 0; b < S; b++)
        for (let a = 0; a < S; a++) {
          const k = (y * S + b) * W + x * S + a;
          if (d[k] > thr) {
            dg.owner[k] = 0;
            any = true;
          }
        }
    }
  if (!any) return;
  const dirs = st.data.tones?.directions ?? { s45: true, s21: false, s12: false };
  const ds = classifyCells(dg, (o) => o === 0, (id) => shapeKindAllowed(id, dirs)).shape;
  // the art cannot draw dark features thinner than a cell: drop spurs and lone partial cells
  for (let pass = 0; pass < 2; pass++)
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const ci = y * w + x;
        const v = ds[ci];
        if (v === EMPTY) continue;
        const full = (i: number, j: number) => i >= 0 && j >= 0 && i < w && j < h && ds[j * w + i] === FULL;
        const hN = (full(x - 1, y) ? 1 : 0) + (full(x + 1, y) ? 1 : 0);
        const vN = (full(x, y - 1) ? 1 : 0) + (full(x, y + 1) ? 1 : 0);
        if (v === FULL ? hN === 0 || vN === 0 : hN + vN === 0) ds[ci] = EMPTY;
      }
  const dAt = (x: number, y: number) => (x < 0 || y < 0 || x >= w || y >= h ? EMPTY : ds[y * w + x]);
  const nb = new Array<number>(8);
  const keys: string[] = [];
  const mkeys: string[] = [];
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const ci = y * w + x;
      if (!isFill[ci]) continue;
      const own = ds[ci];
      let near = own !== EMPTY;
      for (let i = 0; i < 8; i++) {
        nb[i] = dAt(x + NB[i][0], y + NB[i][1]);
        if (nb[i] !== EMPTY) near = true;
      }
      if (!near) continue;
      const seed = shapes[ownerCell[ci]]?.shading?.seed ?? 1;
      const r = hash(x + x0, y + y0, seed, 23);
      const dist = lookup(st.toneRules, own, nb, keys, mkeys);
      let ch: number;
      if (dist) ch = sample(dist, null, undefined, undefined, r);
      else if (own === EMPTY) continue;
      else ch = nearestTone(st, own, r);
      // plain light tiles keep the base fill pattern
      if (st.lightTones.has(ch)) continue;
      lvl[ci] = ch;
    }
}

/** Tone tile whose darkness grid is closest to the shape's coverage (fallback for unseen contexts). */
function nearestTone(st: CompiledStyle, shape: number, r: number): number {
  const want = new Float32Array(16);
  for (let b = 0; b < 4; b++)
    for (let a = 0; a < 4; a++) {
      let n = 0;
      for (let v = 0; v < 2; v++) for (let u = 0; u < 2; u++) if (insideShape(shape, (a + (u + 0.5) / 2) / 4, (b + (v + 0.5) / 2) / 4)) n++;
      want[b * 4 + a] = n / 4;
    }
  let bestE = Infinity;
  for (const t of st.tones) {
    let e = 0;
    for (let i = 0; i < 16; i++) e += Math.abs(t.g[i] - want[i]);
    if (e < bestE) bestE = e;
  }
  const cand = st.tones.filter((t) => {
    let e = 0;
    for (let i = 0; i < 16; i++) e += Math.abs(t.g[i] - want[i]);
    return e <= bestE + 0.35;
  });
  let total = 0;
  for (const t of cand) total += t.w;
  let x = r * total;
  for (const t of cand) {
    x -= t.w;
    if (x < 0) return t.c;
  }
  return cand[0].c;
}

/** debugging hook: called for every placed stamp (stamp index, anchor cell) */
export const debugHooks: {
  stamp: ((index: number, x: number, y: number) => void) | null;
  /** rule decision: layer, cell, context key used (suffix ~ = mirrored), chosen choice */
  rule: ((layer: string, x: number, y: number, key: string, choice: number) => void) | null;
} = { stamp: null, rule: null };

function placeStamps(
  gr: { style: CompiledStyle; targets: TerrainShape['targets'] },
  shapes: TerrainShape[],
  w: number,
  h: number,
  x0: number,
  y0: number,
  shape: Int8Array,
  ownerCell: Int16Array,
  at: (x: number, y: number) => number,
  lvl: Int32Array,
  isFill: Uint8Array,
  fg: Int32Array,
  bg: Int32Array,
  foliage: Map<number, number> | null,
  bareLines: Uint8Array | null,
) {
  const st = gr.style;
  if (!st.stamps.length) return;
  // a surface decoration drawn next to a line marked bare, or in a cell with one, would stand on it
  const bareAt = (x: number, y: number, line: number) => x >= 0 && y >= 0 && x < w && y < h && (bareLines![y * w + x] & line) !== 0;
  const onBare = (s: CompiledStamp, x: number, y: number) => {
    const test = (nx: number, ny: number) =>
      bareAt(nx, ny, ALL_LINES) || bareAt(nx - 1, ny, LINE_R) || bareAt(nx + 1, ny, LINE_L) || bareAt(nx, ny - 1, LINE_T) || bareAt(nx, ny + 1, LINE_B);
    for (let j = 0; j < s.tiles.length; j += 3) if (test(x + s.tiles[j], y + s.tiles[j + 1])) return true;
    for (let j = 0; j < s.cover.length; j += 2) if (test(x + s.cover[j], y + s.cover[j + 1])) return true;
    return false;
  };
  const occ = { level: new Uint8Array(w * h), fg: new Uint8Array(w * h), bg: new Uint8Array(w * h) };
  for (let i = 0; i < fg.length; i++) if (fg[i] >= 0) occ.fg[i] = 1;
  // painted foliage per grid cell and decoration layer: kinds to add, kinds to keep out
  type Grids = { add: Uint8Array; rem: Uint8Array };
  let paint: Record<FoliageLayer, Grids> | null = null;
  if (foliage?.size) {
    const grids = (): Grids => ({ add: new Uint8Array(w * h), rem: new Uint8Array(w * h) });
    paint = { front: grids(), back: grids() };
    for (const [k, v] of foliage) {
      const x = Math.floor(k / 65536) - 32768 - x0, y = (k % 65536) - 32768 - y0;
      if (x < 0 || y < 0 || x >= w || y >= h) continue;
      for (const l of ['front', 'back'] as const) [paint[l].add[y * w + x], paint[l].rem[y * w + x]] = foliageFields(v, l);
    }
  }
  const paintOf = (s: CompiledStamp) => paint![s.layer === 'bg' ? 'back' : 'front'];
  // added paint counts where a decoration is rooted (its anchor and the ground it stands on);
  // removed paint anywhere it would draw or cover
  const cellBits = (m: Uint8Array, x: number, y: number, s: CompiledStamp, rooted: boolean) => {
    let any = 0;
    const one = (nx: number, ny: number) => {
      if (nx >= 0 && ny >= 0 && nx < w && ny < h) any |= m[ny * w + nx];
    };
    one(x, y);
    if (rooted) {
      for (let j = 0; j < s.req.length; j += 4) if (s.req[j + 3] !== 1) one(x + s.req[j], y + s.req[j + 1]);
    } else {
      for (let j = 0; j < s.tiles.length; j += 3) one(x + s.tiles[j], y + s.tiles[j + 1]);
      for (let j = 0; j < s.cover.length; j += 2) one(x + s.cover[j], y + s.cover[j + 1]);
    }
    return any;
  };
  const fits = (s: CompiledStamp, x: number, y: number, painted = false) => {
    if (bareLines && !painted && s.layer !== 'level' && onBare(s, x, y)) return false;
    const r = s.req;
    for (let j = 0; j < r.length; j += 4) {
      const v = at(x + r[j], y + r[j + 1]);
      if (r[j + 3] === 1 ? v !== EMPTY : v !== r[j + 2]) return false;
    }
    if (paint && cellBits(paintOf(s).rem, x, y, s, false) & s.bit) return false;
    const o = occ[s.layer];
    for (let j = 0; j < s.cover.length; j += 2) {
      const nx = x + s.cover[j], ny = y + s.cover[j + 1];
      if (nx < 0 || ny < 0 || nx >= w || ny >= h || o[ny * w + nx]) return false;
    }
    for (let j = 0; j < s.tiles.length; j += 3) {
      const nx = x + s.tiles[j], ny = y + s.tiles[j + 1];
      if (nx < 0 || ny < 0 || nx >= w || ny >= h || o[ny * w + nx]) return false;
      if (s.layer === 'level' && !isFill[ny * w + nx] && lvl[ny * w + nx] !== NONE) return false;
    }
    return true;
  };
  const place = (s: CompiledStamp, x: number, y: number) => {
    debugHooks.stamp?.(s.index, x + x0, y + y0);
    const o = occ[s.layer];
    const dst = s.layer === 'level' ? lvl : s.layer === 'fg' ? fg : bg;
    for (let j = 0; j < s.tiles.length; j += 3) {
      const ni = (y + s.tiles[j + 1]) * w + x + s.tiles[j];
      dst[ni] = s.tiles[j + 2];
      o[ni] = 1;
    }
    for (let j = 0; j < s.cover.length; j += 2) o[(y + s.cover[j + 1]) * w + x + s.cover[j]] = 1;
  };
  const hasTarget = (s: CompiledStamp) => (s.layer === 'fg' ? !!gr.targets.foreground : s.layer === 'bg' ? !!gr.targets.background : true);
  const decoAt = (ci: number) => shapes[ownerCell[ci]]?.decoration ?? st.defaultDecoration;
  /** settings of the polygon the stamp stands on (its root cell), else of the anchor's owner */
  const stampDeco = (s: CompiledStamp, x: number, y: number, fallback: DecorationSettings) => {
    if (!s.root) return fallback;
    const rx = x + s.root[0], ry = y + s.root[1];
    if (rx < 0 || ry < 0 || rx >= w || ry >= h || ownerCell[ry * w + rx] < 0) return fallback;
    return decoAt(ry * w + rx);
  };
  // anchors: cells near the terrain
  const near = new Uint8Array(w * h);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      if (shape[y * w + x] === EMPTY) continue;
      for (let dy = -2; dy <= 2; dy++)
        for (let dx = -2; dx <= 2; dx++) {
          const nx = x + dx, ny = y + dy;
          if (nx >= 0 && ny >= 0 && nx < w && ny < h) near[ny * w + nx] = 1;
        }
    }
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const ci = y * w + x;
      if (!near[ci] || ownerCell[ci] < 0) continue;
      const deco = decoAt(ci);
      const own = shape[ci];
      const cands = st.stampsByAnchor.get(own);
      const loose = st.stampsByAnchor.get(-1);
      const n1 = cands?.length ?? 0;
      const sn = n1 + (loose?.length ?? 0);
      if (!sn) continue;
      const nth = (k: number) => (k < n1 ? cands![k] : loose![k - n1]);
      // painted foliage: one of the decorations of the painted kinds that fit here, picked by how often
      // the levels use it
      if (paint) {
        let pick: CompiledStamp | null = null, total = 0;
        for (let k = 0; k < sn; k++) {
          const s = nth(k);
          if (!(cellBits(paintOf(s).add, x, y, s, true) & s.bit) || !hasTarget(s)) continue;
          // painting adds to a polygon even with its decoration unticked, but a slider at 0 turns it off
          const sd = stampDeco(s, x, y, deco);
          if (sd.density <= 0 || (sd.kinds?.[s.kind] ?? 1) <= 0 || !fits(s, x, y, true)) continue;
          const wgt = Math.max(s.rate, 0.002);
          total += wgt;
          if (hash(x + x0, y + y0, deco.seed * 31 + s.index, 59) * total < wgt) pick = s;
        }
        if (pick) place(pick, x, y);
      }
      // rotate the candidates per cell so that at high density the first ones do not take every spot
      const start = Math.floor(hash(x + x0, y + y0, deco.seed, 53) * sn);
      for (let k = 0; k < sn; k++) {
        const s = nth((start + k) % sn);
        const sd = stampDeco(s, x, y, deco);
        if (!sd.enabled || sd.density <= 0) continue;
        const layerOn = s.layer === 'fg' ? sd.foreground && gr.targets.foreground : s.layer === 'bg' ? sd.background && gr.targets.background : sd.background;
        if (!layerOn) continue;
        const mult = sd.density * (sd.kinds?.[s.kind] ?? 1);
        if (mult <= 0 || hash(x + x0, y + y0, sd.seed * 7919 + s.index, 31) >= s.rate * mult) continue;
        if (!fits(s, x, y)) continue;
        place(s, x, y);
      }
    }
}
